import { Suspense } from 'react';
import { useRoutes, RouteObject } from 'react-router-dom';
import { addLocale } from 'primereact/api';
import { Helmet, HelmetProvider } from 'react-helmet-async';

import PATH from './routes/path';
import RoleProtectedRoute from '@/components/shared/RoleProtectedRoute';
import AuthenticatedLayout from '@/components/shared/AuthenticatedLayout';
import Loader from './components/common/loader/Loader';
import StudentManagementPage from '@/pages/student-management';
import CoachManagementPage from '@/pages/coach-management';
import StudentManagementEditPage from '@/pages/student-management/edit';
import LoginPage from '@/pages/login';
import ForgotPasswordPage from '@/pages/forgot-password';
import StudentManagementNewPage from '@/pages/student-management/new';
import CoachManagementNewPage from '@/pages/coach-management/new';
import CoachManagementEditPage from '@/pages/coach-management/edit';
import MentorManagementPage from '@/pages/mentor-management';
import MentorManagementNewPage from '@/pages/mentor-management/new';
import MentorManagementEditPage from '@/pages/mentor-management/edit';
import MentorCoachManagementPage from '@/pages/mentor-coach-management';
import MentorLessonManagementPage from '@/pages/mentor-lesson-management';
import MentorLessonProgressPage from '@/pages/mentor-lesson-management/progress';
import HomeRedirect from '@/components/shared/HomeRedirect';
import ForbiddenPage from '@/pages/403';
import NotFoundPage from '@/pages/404';
import InternalServerErrorPage from '@/pages/500';
import ResetPasswordPage from '@/pages/reset-password';
import Lesson from '@/pages/lesson/';
import LessonNewPage from '@/pages/lesson/new';
import { ROLE } from './shared/constants/user';
import LessonEditPage from './pages/lesson/edit';
import ResetPasswordSuccessPage from './pages/reset-password-success';
import VersionManagementPage from './pages/version-management';
import { useTranslation } from './config/i18n';
import MaintainPage from './pages/503';
import LessonProgress from './pages/lesson/progress/LessonProgress';
import { Conversation } from './pages/chat';
import GiftManagementPage from './pages/gift-management';
import GiftManagementNewPage from './pages/gift-management/new';
import GiftManagementEditPage from './pages/gift-management/edit';
import GraduationManagementPage from './pages/graduation-management';
import GraduationManagementNewPage from './pages/graduation-management/new';
import GraduationManagementEditPage from './pages/graduation-management/edit';
import PlanPage from './pages/plan';
import PlanNewPage from './pages/plan/new';
import PlanEditPage from './pages/plan/edit';
import PaymentPage from './pages/payment';
import RegisterAccountPage from './pages/register-account';
import UserManagementPage from './pages/user-management';
import UserManagementEditPage from './pages/user-management/edit';
import ProfilePage from './pages/profile';
import PlanProfilePage from './pages/profile/plan';

function AppRouter() {
  const [t] = useTranslation('');

  addLocale('ja', {
    firstDayOfWeek: 0,
    dayNames: [
      '日曜日',
      '月曜日',
      '火曜日',
      '水曜日',
      '木曜日',
      '金曜日',
      '土曜日',
    ],
    dayNamesShort: ['日', '月', '火', '水', '木', '金', '土'],
    dayNamesMin: ['日', '月', '火', '水', '木', '金', '土'],
    monthNames: [
      '1月',
      '2月',
      '3月',
      '4月',
      '5月',
      '6月',
      '7月',
      '8月',
      '9月',
      '10月',
      '11月',
      '12月',
    ],
    monthNamesShort: [
      '1月',
      '2月',
      '3月',
      '4月',
      '5月',
      '6月',
      '7月',
      '8月',
      '9月',
      '10月',
      '11月',
      '12月',
    ],
    today: '今日',
    clear: 'クリア',
    dateFormat: 'yy/mm/dd',
    weekHeader: '週',
  });

  const routesForPublic = [
    // No `path: ''` entry here. It ranks equal to the authenticated `path: '/'`
    // route and, being spread first, always won the tie — which made the root
    // route dead code that sent signed-in users to the login form. Signed-out
    // visitors still reach the login form: RoleProtectedRoute redirects them.
    {
      path: PATH.login,
      element: (
        <>
          <Helmet>
            <title>{t('common.system_title')}</title>
          </Helmet>
          <LoginPage />
        </>
      ),
    },
    {
      path: PATH.forgot_password,
      element: (
        <>
          <Helmet>
            <title>{t('common.system_title')}</title>
          </Helmet>
          <ForgotPasswordPage />
        </>
      ),
    },
    {
      path: PATH.reset_password,
      element: (
        <>
          <Helmet>
            <title>{t('common.system_title')}</title>
          </Helmet>
          <ResetPasswordPage />
        </>
      ),
    },
    {
      path: PATH.reset_password_success,
      element: (
        <>
          <Helmet>
            <title>{t('common.system_title')}</title>
          </Helmet>
          <ResetPasswordSuccessPage />
        </>
      ),
    },
    {
      path: PATH.forbidden,
      element: (
        <>
          <Helmet>
            <title>{t('common.system_title')}</title>
          </Helmet>
          <ForbiddenPage />
        </>
      ),
    },
    {
      path: PATH.internal_server_error,
      element: (
        <>
          <Helmet>
            <title>{t('common.system_title')}</title>
          </Helmet>
          <InternalServerErrorPage />
        </>
      ),
    },
    {
      path: PATH.maintain,
      element: (
        <>
          <Helmet>
            <title>{t('common.system_title')}</title>
          </Helmet>
          <MaintainPage />
        </>
      ),
    },
    {
      path: '*',
      element: (
        <>
          <Helmet>
            <title>{t('common.system_title')}</title>
          </Helmet>
          <NotFoundPage />
        </>
      ),
    },
  ];

  const routesForAuthenticated: RouteObject[] = [
    {
      path: '/',
      element: (
        // Every signed-in role, because getRedirectPathByUser has a destination
        // for each. Narrowing this list would send the omitted role to /403 on
        // the app's own root URL.
        <RoleProtectedRoute
          requiredRoles={[
            ROLE.SUPPER_ADMIN,
            ROLE.ADMIN,
            ROLE.MENTOR,
            ROLE.TEACHER,
          ]}
        >
          <HomeRedirect />
        </RoleProtectedRoute>
      ),
    },
    {
      path: PATH.student_management,
      element: (
        <RoleProtectedRoute
          requiredRoles={[ROLE.SUPPER_ADMIN, ROLE.ADMIN, ROLE.TEACHER]}
        >
          <AuthenticatedLayout />
        </RoleProtectedRoute>
      ),
      children: [
        {
          path: '',
          element: (
            <>
              <Helmet>
                <title>{t('menu.student_management')}</title>
              </Helmet>
              <StudentManagementPage />
            </>
          ),
        },
        {
          path: PATH.student_management_new,
          element: (
            <>
              <Helmet>
                <title>{t('user.new_student')}</title>
              </Helmet>
              <StudentManagementNewPage />
            </>
          ),
        },
        {
          path: `${PATH.student_management_edit}/:id`,
          element: (
            <>
              <Helmet>
                <title>{t('user.edit_student')}</title>
              </Helmet>
              <StudentManagementEditPage />
            </>
          ),
        },
      ],
    },
    {
      path: PATH.coach_management,
      element: (
        <RoleProtectedRoute requiredRoles={[ROLE.SUPPER_ADMIN, ROLE.ADMIN]}>
          <AuthenticatedLayout />
        </RoleProtectedRoute>
      ),
      children: [
        {
          path: '',
          element: (
            <>
              <Helmet>
                <title>{t('menu.coach_management')}</title>
              </Helmet>
              <CoachManagementPage />
            </>
          ),
        },
        {
          path: PATH.coach_management_new,
          element: (
            <>
              <Helmet>
                <title>{t('user.new_coach')}</title>
              </Helmet>
              <CoachManagementNewPage />
            </>
          ),
        },
        {
          path: `${PATH.coach_management_edit}/:id`,
          element: (
            <>
              <Helmet>
                <title>{t('user.edit_coach')}</title>
              </Helmet>
              <CoachManagementEditPage />
            </>
          ),
        },
      ],
    },
    {
      path: PATH.mentor_management,
      element: (
        <RoleProtectedRoute requiredRoles={[ROLE.SUPPER_ADMIN, ROLE.ADMIN]}>
          <AuthenticatedLayout />
        </RoleProtectedRoute>
      ),
      children: [
        {
          path: '',
          element: (
            <>
              <Helmet>
                <title>{t('menu.mentor_management')}</title>
              </Helmet>
              <MentorManagementPage />
            </>
          ),
        },
        {
          path: PATH.mentor_management_new,
          element: (
            <>
              <Helmet>
                <title>{t('user.new_mentor')}</title>
              </Helmet>
              <MentorManagementNewPage />
            </>
          ),
        },
        {
          path: `${PATH.mentor_management_edit}/:id`,
          element: (
            <>
              <Helmet>
                <title>{t('user.edit_mentor')}</title>
              </Helmet>
              <MentorManagementEditPage />
            </>
          ),
        },
      ],
    },
    {
      // Read-only screens for the mentor themself. Deliberately separate routes
      // so no mentor path can reach a create/edit screen through guard
      // inheritance.
      path: PATH.mentor_coach_management,
      element: (
        <RoleProtectedRoute requiredRoles={[ROLE.MENTOR]}>
          <AuthenticatedLayout />
        </RoleProtectedRoute>
      ),
      children: [
        {
          path: '',
          element: (
            <>
              <Helmet>
                <title>{t('menu.mentor_coach_management')}</title>
              </Helmet>
              <MentorCoachManagementPage />
            </>
          ),
        },
      ],
    },
    {
      path: PATH.mentor_lesson_management,
      element: (
        <RoleProtectedRoute requiredRoles={[ROLE.MENTOR]}>
          <AuthenticatedLayout />
        </RoleProtectedRoute>
      ),
      children: [
        {
          path: '',
          element: (
            <>
              <Helmet>
                <title>{t('menu.mentor_lesson_management')}</title>
              </Helmet>
              <MentorLessonManagementPage />
            </>
          ),
        },
        {
          // Param must be named `id` — CoachProgress reads useParams<{ id }>().
          path: PATH.mentor_lesson_progress,
          element: (
            <>
              <Helmet>
                <title>{t('lesson_progress.page_title')}</title>
              </Helmet>
              <MentorLessonProgressPage />
            </>
          ),
        },
      ],
    },
    {
      path: PATH.lesson,
      element: (
        <RoleProtectedRoute
          requiredRoles={[ROLE.SUPPER_ADMIN, ROLE.ADMIN, ROLE.TEACHER]}
        >
          <AuthenticatedLayout />
        </RoleProtectedRoute>
      ),
      children: [
        {
          path: '',
          element: (
            <>
              <Helmet>
                <title>{t('menu.lesson')}</title>
              </Helmet>
              <Lesson />
            </>
          ),
        },
        {
          path: PATH.lesson_new,
          element: (
            <RoleProtectedRoute
              requiredRoles={[ROLE.SUPPER_ADMIN, ROLE.ADMIN, ROLE.TEACHER]}
            >
              <Helmet>
                <title>{t('lesson_list.new')}</title>
              </Helmet>
              <LessonNewPage />
            </RoleProtectedRoute>
          ),
        },
        {
          path: `${PATH.lesson_edit}/:id`,
          element: (
            <>
              <Helmet>
                <title>{t('lesson.edit_title')}</title>
              </Helmet>
              <LessonEditPage />
            </>
          ),
        },
        {
          path: `${PATH.lesson}/:id/progress`,
          element: (
            <>
              <Helmet>
                <title>{t('lesson.edit_title')}</title>
              </Helmet>
              <LessonProgress />
            </>
          ),
        },
      ],
    },
    {
      path: PATH.version_management,
      element: (
        <RoleProtectedRoute requiredRoles={[ROLE.SUPPER_ADMIN]}>
          <AuthenticatedLayout />
        </RoleProtectedRoute>
      ),
      children: [
        {
          path: '',
          element: (
            <>
              <Helmet>
                <title>{t('version.title')}</title>
              </Helmet>
              <VersionManagementPage />
            </>
          ),
        },
      ],
    },
    {
      path: PATH.chat,
      element: (
        <RoleProtectedRoute
          requiredRoles={[
            ROLE.SUPPER_ADMIN,
            ROLE.ADMIN,
            ROLE.TEACHER,
            ROLE.MENTOR,
          ]}
        >
          <AuthenticatedLayout />
        </RoleProtectedRoute>
      ),
      children: [
        {
          path: PATH.chat_conversation,
          element: (
            <>
              <Helmet>
                <title>{t('menu.chat')}</title>
              </Helmet>
              <Conversation />
            </>
          ),
        },
        {
          path: `${PATH.chat_conversation}/:conversationId`,
          element: (
            <>
              <Helmet>
                <title>{t('menu.chat')}</title>
              </Helmet>
              <Conversation />
            </>
          ),
        },
      ],
    },
    {
      path: PATH.gift,
      element: (
        <RoleProtectedRoute requiredRoles={[ROLE.SUPPER_ADMIN, ROLE.ADMIN]}>
          <AuthenticatedLayout />
        </RoleProtectedRoute>
      ),
      children: [
        {
          path: '',
          element: (
            <>
              <Helmet>
                <title>{t('menu.gift')}</title>
              </Helmet>
              <GiftManagementPage />
            </>
          ),
        },
        {
          path: PATH.gift_new,
          element: (
            <>
              <Helmet>
                <title>{t('gift.add_new')}</title>
              </Helmet>
              <GiftManagementNewPage />
            </>
          ),
        },
        {
          path: `${PATH.gift_edit}/:id`,
          element: (
            <>
              <Helmet>
                <title>{t('gift.edit_gift')}</title>
              </Helmet>
              <GiftManagementEditPage />
            </>
          ),
        },
      ],
    },
    {
      path: PATH.graduation,
      element: (
        <RoleProtectedRoute requiredRoles={[ROLE.SUPPER_ADMIN, ROLE.ADMIN]}>
          <AuthenticatedLayout />
        </RoleProtectedRoute>
      ),
      children: [
        {
          path: '',
          element: (
            <>
              <Helmet>
                <title>{t('menu.graduation')}</title>
              </Helmet>
              <GraduationManagementPage />
            </>
          ),
        },
        {
          path: PATH.graduation_new,
          element: (
            <>
              <Helmet>
                <title>{t('graduation.add_new')}</title>
              </Helmet>
              <GraduationManagementNewPage />
            </>
          ),
        },
        {
          path: `${PATH.graduation_edit}/:id`,
          element: (
            <>
              <Helmet>
                <title>{t('graduation.edit')}</title>
              </Helmet>
              <GraduationManagementEditPage />
            </>
          ),
        },
      ],
    },
    {
      path: PATH.plan,
      element: (
        <RoleProtectedRoute requiredRoles={[ROLE.SUPPER_ADMIN, ROLE.ADMIN]}>
          <AuthenticatedLayout />
        </RoleProtectedRoute>
      ),
      children: [
        {
          path: '',
          element: (
            <>
              <Helmet>
                <title>{t('menu.plan')}</title>
              </Helmet>
              <PlanPage />
            </>
          ),
        },
        {
          path: PATH.plan_new,
          element: (
            <>
              <Helmet>
                <title>{t('plan.add_new')}</title>
              </Helmet>
              <PlanNewPage />
            </>
          ),
        },
        {
          path: `${PATH.plan_edit}/:id`,
          element: (
            <>
              <Helmet>
                <title>{t('plan.edit')}</title>
              </Helmet>
              <PlanEditPage />
            </>
          ),
        },
      ],
    },
    {
      path: PATH.payment,
      element: (
        <RoleProtectedRoute requiredRoles={[ROLE.SUPPER_ADMIN, ROLE.ADMIN]}>
          <AuthenticatedLayout />
        </RoleProtectedRoute>
      ),
      children: [
        {
          path: '',
          element: (
            <>
              <Helmet>
                <title>{t('menu.payment')}</title>
              </Helmet>
              <PaymentPage />
            </>
          ),
        },
      ],
    },
    {
      path: PATH.register_account,
      element: (
        <>
          <Helmet>
            <title>{t('common.system_register_account_title')}</title>
          </Helmet>
          <RegisterAccountPage />
        </>
      ),
    },
    {
      path: PATH.user_management,
      element: (
        <RoleProtectedRoute requiredRoles={[ROLE.SUPPER_ADMIN, ROLE.ADMIN]}>
          <AuthenticatedLayout />
        </RoleProtectedRoute>
      ),
      children: [
        {
          path: '',
          element: (
            <>
              <Helmet>
                <title>{t('menu.user_management')}</title>
              </Helmet>
              <UserManagementPage />
            </>
          ),
        },
        {
          path: `${PATH.user_management_edit}/:id`,
          element: (
            <>
              <Helmet>
                <title>{t('user_management.title')}</title>
              </Helmet>
              <UserManagementEditPage />
            </>
          ),
        },
      ],
    },
    {
      path: PATH.profile,
      element: (
        <RoleProtectedRoute requiredRoles={[ROLE.TEACHER]}>
          <AuthenticatedLayout />
        </RoleProtectedRoute>
      ),
      children: [
        {
          path: '',
          element: (
            <>
              <Helmet>
                <title>{t('profile.title')}</title>
              </Helmet>
              <ProfilePage />
            </>
          ),
        },
        {
          path: PATH.profile_change_plan,
          element: (
            <>
              <Helmet>
                <title>{t('profile.plan_top_header')}</title>
              </Helmet>
              <PlanProfilePage />
            </>
          ),
        },
      ]
    }
  ];

  const routes = [...routesForPublic, ...routesForAuthenticated];

  const routing = useRoutes(routes);

  return (
    <HelmetProvider>
      <Suspense fallback={<Loader isLoading={true} />}>{routing}</Suspense>
    </HelmetProvider>
  );
}

export default AppRouter;
